/**
 * ShareQuoteButton — iter197mm
 * ============================
 * One-tap share for the customer-portal quote URL. Surfaces a compact
 * button at the top of the portal; tapping it opens the native share
 * sheet on mobile (Android / iOS WebShare API) or falls back to a small
 * dropdown with WhatsApp, Email and Copy-Link actions on desktop.
 *
 * Why it ships:
 *   - Customers often want a partner / fleet manager / family member to
 *     co-sign a quote. Without this button they copy-paste the URL by
 *     hand — friction that costs approvals.
 *   - WhatsApp + email are the two channels used 95%+ of the time in
 *     the UK consumer auto-repair space.
 *
 * Multi-tenant ready: uses the garage name passed in from the portal
 * payload so the share text reads "Quote from {Garage Name}" rather
 * than a hardcoded brand.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Share2, Mail, Link2, Check } from 'lucide-react';

const ShareQuoteButton = ({ garageName, vehicleReg }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrapperRef = useRef(null);

  const portalUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = `Quote from ${garageName || 'your garage'}`;
  const shareText = vehicleReg
    ? `Here's the quote for ${vehicleReg.toUpperCase()} — review and approve work directly:`
    : `Here's the quote from ${garageName || 'your garage'} — review and approve work directly:`;

  // Native share sheet on mobile (iOS Safari, Android Chrome).
  const hasNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  const handleNativeShare = async () => {
    try {
      await navigator.share({
        title: shareTitle,
        text:  shareText,
        url:   portalUrl,
      });
    } catch (_) {
      // User cancelled — silent.
    }
  };

  const handleWhatsApp = () => {
    const msg = encodeURIComponent(`${shareText}\n${portalUrl}`);
    window.open(`https://wa.me/?text=${msg}`, '_blank', 'noopener,noreferrer');
    setOpen(false);
  };

  const handleEmail = () => {
    const subject = encodeURIComponent(shareTitle);
    const body    = encodeURIComponent(`${shareText}\n\n${portalUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    setOpen(false);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(portalUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      // Older browsers — fall back to a hidden textarea.
      const ta = document.createElement('textarea');
      ta.value = portalUrl;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (e) { /* noop */ }
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  // Close dropdown on outside click.
  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const handlePrimaryClick = () => {
    if (hasNativeShare) {
      handleNativeShare();
    } else {
      setOpen((v) => !v);
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        onClick={handlePrimaryClick}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-200 transition-colors"
        data-testid="portal-share-button"
        aria-label="Share this quote"
      >
        <Share2 className="w-3.5 h-3.5" />
        Share
      </button>

      {open && !hasNativeShare && (
        <div
          className="absolute right-0 mt-2 w-48 rounded-lg border border-slate-200 bg-white shadow-lg z-30 overflow-hidden"
          data-testid="portal-share-dropdown"
        >
          <button
            onClick={handleWhatsApp}
            className="flex items-center gap-2 w-full px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 text-left"
            data-testid="portal-share-whatsapp"
          >
            {/* Brand-style WhatsApp glyph (inline SVG so we don't pull
                in a new icon dependency). */}
            <svg className="w-4 h-4 text-emerald-600" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M.057 24l1.687-6.163A11.867 11.867 0 010 11.86C0 5.323 5.31 0 11.853 0c3.205 0 6.215 1.249 8.473 3.514a11.821 11.821 0 013.51 8.418c0 6.537-5.32 11.86-11.857 11.86h-.005a11.876 11.876 0 01-5.667-1.442L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.89 9.884a9.86 9.86 0 001.515 5.26l-.999 3.648 3.974-1.607zm11.387-5.464c-.074-.123-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.148-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.019-.458.13-.606.134-.133.297-.347.446-.521.149-.173.198-.297.297-.495.099-.198.05-.371-.025-.52-.075-.149-.669-1.611-.916-2.206-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.371-.272.297-1.04 1.016-1.04 2.479s1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
            </svg>
            WhatsApp
          </button>
          <button
            onClick={handleEmail}
            className="flex items-center gap-2 w-full px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 text-left"
            data-testid="portal-share-email"
          >
            <Mail className="w-4 h-4 text-slate-500" />
            Email
          </button>
          <button
            onClick={handleCopy}
            className="flex items-center gap-2 w-full px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 text-left border-t border-slate-100"
            data-testid="portal-share-copy"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 text-emerald-600" />
                <span className="text-emerald-700">Link copied</span>
              </>
            ) : (
              <>
                <Link2 className="w-4 h-4 text-slate-500" />
                Copy link
              </>
            )}
          </button>
        </div>
      )}

      {/* Native-share path: also show "copied" toast when user uses
          Copy-Link via native sheet. Hidden by default. */}
      {copied && hasNativeShare && (
        <div
          className="absolute right-0 mt-2 px-3 py-2 rounded-md bg-slate-900 text-white text-xs font-medium shadow-lg"
          data-testid="portal-share-copied-toast"
        >
          Link copied
        </div>
      )}
    </div>
  );
};

export default ShareQuoteButton;
